import React, { useEffect, useState } from 'react';

import { MtxIcon } from '@meilleurtaux/mtp-uikit-react';
import { useTranslation } from 'react-i18next';

import { useGetApiArbitrageAvenant } from '@/api/arbitrage/arbitrage';
import base64ToBlob from '@/utils/functions/base64ToBlob/base64ToBlob';
import { log } from '@/utils/functions/logger/logger';

import styles from './styles.module.css';

import type { DocumentResponse } from '@/api/placementApi.schemas';

interface QpsAvenantLinkProps {
  arbitrageId: string;
  codeSupport: string;
  reference: string;
}

// Module-level, hence stable: React Query decodes once per response. A corrupted payload gives
// `null`, it only hides the encart and must not take the questionnaire down.
const selectAvenantPdf = (avenant: DocumentResponse): Blob | null => {
  if (!avenant.success || !avenant.body) return null;
  try {
    return base64ToBlob(avenant.body, 'application/pdf');
  } catch (error) {
    log.error('[qps] Avenant pdf is not valid base64', error);
    return null;
  }
};

/**
 * Link to the avenant of the questionnaire's support, opened in a new tab: a plain `<a>` to a blob
 * URL, nothing to wait for on click, no popup to be blocked. Hidden while loading and when the
 * avenant cannot be loaded or read.
 */
const QpsAvenantLink: React.FC<QpsAvenantLinkProps> = ({ arbitrageId, codeSupport, reference }) => {
  const { t } = useTranslation();
  const { data: pdf } = useGetApiArbitrageAvenant(
    arbitrageId,
    { codeSupport, reference },
    { query: { select: selectAvenantPdf } }
  );
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!pdf) return;
    const blobUrl = URL.createObjectURL(pdf);
    setUrl(blobUrl);
    return () => URL.revokeObjectURL(blobUrl);
  }, [pdf]);

  // Rendered with the decoded pdf, not with its URL: the question screen can mount from an effect,
  // whose own effects run after the paint. The href follows a render later.
  if (!pdf) return null;

  return (
    <a href={url ?? undefined} target="_blank" rel="noopener noreferrer" className={styles.link}>
      <span className={styles.content}>
        <span className={styles.icon} aria-hidden="true">
          <MtxIcon name="document" size={24} />
        </span>
        <span className={styles.label}>
          {t('qps.question.avenant')}{' '}
          {/* L'icône « lien externe » est décorative : le format et le nouvel onglet sont dits ici. */}
          <span className={styles.srOnly}>{t('qps.question.avenantNewTab')}</span>
        </span>
      </span>
      <span className={styles.icon} aria-hidden="true">
        <MtxIcon name="external-link" size={16} />
      </span>
    </a>
  );
};

export default QpsAvenantLink;
